import { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { AlertBar, Icon, Typography } from '@packages/ui/shared';

const POPUP_GUIDANCE_DISMISSED_KEY = 'stis-sd-popup-guidance-dismissed';

export const isPopupGuidanceDismissed = (): boolean => {
    try {
        return window.localStorage.getItem(POPUP_GUIDANCE_DISMISSED_KEY) === 'true';
    } catch {
        return false;
    }
};

export const persistPopupGuidanceDismissed = (): void => {
    try {
        window.localStorage.setItem(POPUP_GUIDANCE_DISMISSED_KEY, 'true');
    } catch {
        // localStorage unavailable (private mode) — banner will reappear next visit
    }
};

type BrowserKey = 'chrome' | 'edge' | 'safari' | 'firefox';

// Order matters: Edge UA contains "Chrome/", and Chrome UA contains "Safari/".
const detectBrowser = (): BrowserKey | null => {
    const ua = navigator.userAgent;
    if (/Edg\//.test(ua)) {
        return 'edge';
    }
    if (/Firefox\//.test(ua)) {
        return 'firefox';
    }
    if (/Chrome\//.test(ua)) {
        return 'chrome';
    }
    if (/Safari\//.test(ua)) {
        return 'safari';
    }
    return null;
};

interface BrowserInstruction {
    key: BrowserKey;
    name: string;
    steps: string[];
    /** Internal settings address the user can paste into the address bar. Browsers block navigating to these from a webpage. */
    settingsUrl?: string;
}

const useStyles = createUseStyles({
    bannerBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    instructionsToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        cursor: 'pointer',
        userSelect: 'none',
        marginTop: 4,
        width: 'fit-content',
    },
    instructionsList: {
        margin: '8px 0 0',
        paddingLeft: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        listStyle: 'none',
    },
    browserSteps: {
        margin: '4px 0 0',
        paddingLeft: 18,
    },
    settingsUrlRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 4,
    },
    settingsUrl: {
        fontFamily: 'monospace',
        fontSize: 13,
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: '2px 8px',
        userSelect: 'all',
    },
    copyButton: {
        cursor: 'pointer',
        userSelect: 'none',
    },
});

interface PopupGuidanceBannerProps {
    show: boolean;
    /** True when a pop-up was just blocked — switches to warning styling and copy. */
    blocked: boolean;
    onClose: () => void;
    /** Applied to the banner's outer wrapper (e.g. for page-specific spacing). */
    style?: CSSProperties;
}

export const PopupGuidanceBanner: FC<PopupGuidanceBannerProps> = ({ show, blocked, onClose, style }) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const containerRef = useRef<HTMLDivElement>(null);
    const [instructionsOpen, setInstructionsOpen] = useState(false);
    const [showOtherBrowsers, setShowOtherBrowsers] = useState(false);
    const [copiedKey, setCopiedKey] = useState<BrowserKey | null>(null);

    // When a block is detected the user may be scrolled deep into a results table —
    // bring the warning into view so the feedback isn't missed.
    useEffect(() => {
        if (show && blocked) {
            containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [show, blocked]);

    if (!show) {
        return null;
    }

    const browserInstructions: BrowserInstruction[] = [
        {
            key: 'chrome',
            name: t('stisPublications:popupGuidance.chrome.name', 'Chrome'),
            steps: [
                t(
                    'stisPublications:popupGuidance.chrome.step1',
                    'Click the "Pop-up blocked" icon at the right end of the address bar.'
                ),
                t(
                    'stisPublications:popupGuidance.chrome.step2',
                    'Choose "Always allow pop-ups and redirects from this site" and click Done.'
                ),
                t('stisPublications:popupGuidance.chrome.step3', 'Click the link in the document again.'),
            ],
            settingsUrl: 'chrome://settings/content/popups',
        },
        {
            key: 'edge',
            name: t('stisPublications:popupGuidance.edge.name', 'Microsoft Edge'),
            steps: [
                t(
                    'stisPublications:popupGuidance.edge.step1',
                    'Click the "Pop-up blocked" icon at the right end of the address bar.'
                ),
                t(
                    'stisPublications:popupGuidance.edge.step2',
                    'Choose "Always allow pop-ups and redirects from this site" and click Done.'
                ),
                t('stisPublications:popupGuidance.edge.step3', 'Click the link in the document again.'),
            ],
            settingsUrl: 'edge://settings/content/popups',
        },
        {
            key: 'safari',
            name: t('stisPublications:popupGuidance.safari.name', 'Safari'),
            steps: [
                t('stisPublications:popupGuidance.safari.step1', 'Open Safari > Settings > Websites > Pop-up Windows.'),
                t(
                    'stisPublications:popupGuidance.safari.step2',
                    'Find subarutechshare.com and cdn.subarutechshare.com in the list and set them to "Allow".'
                ),
                t('stisPublications:popupGuidance.safari.step3', 'Click the link in the document again.'),
            ],
        },
        {
            key: 'firefox',
            name: t('stisPublications:popupGuidance.firefox.name', 'Firefox'),
            steps: [
                t(
                    'stisPublications:popupGuidance.firefox.step1',
                    'Click the options bar that appears below the address bar when a pop-up is blocked.'
                ),
                t('stisPublications:popupGuidance.firefox.step2', 'Choose "Allow pop-ups for this site".'),
                t('stisPublications:popupGuidance.firefox.step3', 'Click the link in the document again.'),
            ],
            settingsUrl: 'about:preferences#privacy',
        },
    ];

    const detectedBrowser = detectBrowser();
    const detectedInstructions = browserInstructions.filter((item) => item.key === detectedBrowser);
    const otherInstructions = browserInstructions.filter((item) => item.key !== detectedBrowser);
    // Unknown browser: no detected section, list everything under the main toggle.
    const visibleInstructions = detectedBrowser
        ? [...detectedInstructions, ...(showOtherBrowsers ? otherInstructions : [])]
        : browserInstructions;

    const handleCopySettingsUrl = (key: BrowserKey, settingsUrl: string) => {
        navigator.clipboard
            ?.writeText(settingsUrl)
            .then(() => {
                setCopiedKey(key);
                setTimeout(() => setCopiedKey(null), 2000);
            })
            .catch(() => {
                // Clipboard unavailable — the URL is still selectable text
            });
    };

    const renderInstruction = (item: BrowserInstruction) => (
        <li key={item.key}>
            <Typography style={{ fontSize: 14, fontWeight: '600' }}>{item.name}</Typography>
            <ol className={classes.browserSteps}>
                {item.steps.map((step, index) => (
                    <li key={index}>
                        <Typography style={{ fontSize: 14 }}>{step}</Typography>
                    </li>
                ))}
            </ol>
            {item.settingsUrl && (
                <div className={classes.settingsUrlRow}>
                    <Typography style={{ fontSize: 13 }}>
                        {t(
                            'stisPublications:popupGuidance.settingsUrlHint',
                            'Or paste this into your address bar to open pop-up settings:'
                        )}
                    </Typography>
                    <span className={classes.settingsUrl}>{item.settingsUrl}</span>
                    <div className={classes.copyButton}>
                        <Icon
                            name={copiedKey === item.key ? 'checkmark' : 'copy'}
                            color={copiedKey === item.key ? 'greenOne' : 'grayFive'}
                            size={16}
                            onPress={() => handleCopySettingsUrl(item.key, item.settingsUrl!)}
                        />
                    </div>
                </div>
            )}
        </li>
    );

    return (
        <div ref={containerRef} style={style}>
            <AlertBar show={show} info={!blocked} warning={blocked} onClose={onClose}>
                <div className={classes.bannerBody}>
                    <Typography style={{ fontWeight: '600', fontSize: 15 }}>
                        {blocked
                            ? t(
                                  'stisPublications:popupGuidance.blockedTitle',
                                  'Your browser blocked this document from opening.'
                              )
                            : t(
                                  'stisPublications:popupGuidance.title',
                                  'Some links in Service Diagnostics open in new windows.'
                              )}
                    </Typography>
                    <Typography style={{ fontSize: 14 }}>
                        {t(
                            'stisPublications:popupGuidance.body',
                            'Features like DTC Search and Wiring Diagrams inside these documents open results in a new browser window. If clicking a link does nothing, your browser is blocking pop-ups.'
                        )}
                    </Typography>
                    <Typography style={{ fontSize: 14 }}>
                        {t('stisPublications:popupGuidance.domainsPrefix', 'To fix this, allow pop-ups for ')}
                        <Typography style={{ fontWeight: '600', fontSize: 14 }}>subarutechshare.com</Typography>
                        {t('stisPublications:popupGuidance.domainsJoin', ' and ')}
                        <Typography style={{ fontWeight: '600', fontSize: 14 }}>cdn.subarutechshare.com</Typography>
                        {t('stisPublications:popupGuidance.domainsSuffix', ' in your browser settings.')}
                    </Typography>
                    <Typography style={{ fontSize: 14, fontStyle: 'italic', fontWeight: '600', color: '#B45309' }}>
                        {t(
                            'stisPublications:popupGuidance.addressBarTip',
                            'Tip: when a pop-up is blocked, a small "Pop-up blocked" icon appears at the right end of the address bar — click it and choose "Always allow" to fix it right away.'
                        )}
                    </Typography>
                    <div
                        className={classes.instructionsToggle}
                        onClick={() => setInstructionsOpen((prev) => !prev)}
                        role="button"
                        aria-expanded={instructionsOpen}
                    >
                        <Typography style={{ fontSize: 14, fontWeight: '600', color: '#007BFF' }}>
                            {t('stisPublications:popupGuidance.howTo', 'How do I allow pop-ups?')}
                        </Typography>
                        <Icon name={instructionsOpen ? 'chevron-up' : 'chevron-down'} color="blueOne" size={16} />
                    </div>
                    {instructionsOpen && (
                        <>
                            <ul className={classes.instructionsList}>{visibleInstructions.map(renderInstruction)}</ul>
                            {detectedBrowser && otherInstructions.length > 0 && (
                                <div
                                    className={classes.instructionsToggle}
                                    onClick={() => setShowOtherBrowsers((prev) => !prev)}
                                    role="button"
                                    aria-expanded={showOtherBrowsers}
                                >
                                    <Typography style={{ fontSize: 13, fontWeight: '600', color: '#007BFF' }}>
                                        {showOtherBrowsers
                                            ? t(
                                                  'stisPublications:popupGuidance.hideOtherBrowsers',
                                                  'Hide other browsers'
                                              )
                                            : t(
                                                  'stisPublications:popupGuidance.showOtherBrowsers',
                                                  'Using a different browser?'
                                              )}
                                    </Typography>
                                    <Icon
                                        name={showOtherBrowsers ? 'chevron-up' : 'chevron-down'}
                                        color="blueOne"
                                        size={14}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </AlertBar>
        </div>
    );
};
